/**
 * Per-tab module-level registry of company → visibility, populated as MCP tool
 * results stream in (PLT-4548).
 *
 * The chat backend tools (net-impact, DMA, financial effects, …) attach a
 * `visibility: 'private' | 'public'` field to their success responses. The
 * MCP-app iframe card renders a lock badge directly from that field, but the
 * Tufte-style right-margin citation rail in chat prose has no direct access
 * to the tool result payload — it sees only a markdown link the LLM emitted
 * (e.g. `[Storebrand](https://uprightplatform.com/company/storebrand-asa/dma)`).
 *
 * Bridge: when a tool result arrives in the assistant-ui runtime, we also
 * write `(companyId, visibility)` into this module-level map. The platform-
 * link sidenote renderer then parses the `companyId` segment out of the URL
 * and looks up visibility here. Same pattern as `citation-registry.ts` —
 * module-level Map, no React context.
 *
 * Why module-level (not React context):
 *   - The platform-link rendering happens inside `react-markdown`'s rehype
 *     pipeline, where hooks aren't available; the only stable handle into
 *     the shared registry is a module import.
 *   - Tool results race against citation render order; with a lookup-by-id
 *     model we just render a "no badge" sidenote until the result arrives,
 *     then the next render picks up the visibility — no per-message context
 *     plumbing required.
 *   - The map stays bounded for any reasonable session (each entry is two
 *     short strings). `clearVisibilityRegistry` is exposed for tests but
 *     isn't called in production.
 */

export type CompanyVisibility = 'private' | 'public';

export interface RegisteredVisibility {
  visibility: CompanyVisibility;
  /** Optional org name from the MCP response — used in tooltips. */
  ownerOrganizationName?: string;
}

const registry = new Map<string, RegisteredVisibility>();
const listeners = new Set<() => void>();

export function registerCompanyVisibility(companyId: string, entry: RegisteredVisibility): void {
  if (!companyId) return;
  const previous = registry.get(companyId);
  // Notify only when something the lookup-side cares about changes — avoids
  // a re-render storm for tool results that re-confirm the same visibility
  // on every follow-up call.
  const unchanged =
    previous &&
    previous.visibility === entry.visibility &&
    previous.ownerOrganizationName === entry.ownerOrganizationName;
  registry.set(companyId, entry);
  if (!unchanged) {
    for (const l of listeners) l();
  }
}

export function getCompanyVisibility(companyId: string): RegisteredVisibility | undefined {
  return registry.get(companyId);
}

/**
 * Subscribe to registry mutations. Returns an unsubscribe function.
 *
 * Added for the `ChatContextCard` (PLT-4701), which needs to re-render the
 * private-pill suffix on the bound company as soon as the first MCP tool
 * result lands and writes the company's visibility into this registry.
 * The rest of the call sites (markdown sidenote renderer, MCP-app card
 * header) re-render on their own data-driven cadence and don't need to
 * subscribe — but they're not harmed by the listener set existing.
 */
export function subscribeVisibilityRegistry(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only helper. Production code should not rely on the registry being empty. */
export function clearVisibilityRegistry(): void {
  registry.clear();
  for (const l of listeners) l();
}
